import Vue from 'vue';
import types from './types';
import getters from './getters';
import actions from './actions';

const state = {
  conversations: [],
  activeConversationId: null,
  messagesByConversationId: {},
  uiFlags: {
    isFetchingConversations: false,
    isFetchingMessages: false,
    isCreatingConversation: false,
    isSendingMessage: false,
  },
};

const findConversation = (_state, conversationId) =>
  _state.conversations.find(
    conversation => Number(conversation.id) === Number(conversationId)
  );

export const mutations = {
  [types.SET_INTERNAL_CHAT_CONVERSATIONS](_state, conversations) {
    _state.conversations = conversations;
  },

  [types.UPSERT_INTERNAL_CHAT_CONVERSATION](_state, conversation) {
    const index = _state.conversations.findIndex(
      item => Number(item.id) === Number(conversation.id)
    );

    if (index === -1) {
      _state.conversations.push(conversation);
    } else {
      Vue.set(_state.conversations, index, {
        ..._state.conversations[index],
        ...conversation,
      });
    }
  },

  [types.UPDATE_INTERNAL_CHAT_CONVERSATION_ACTIVITY](
    _state,
    { conversationId, lastActivityAt }
  ) {
    const conversation = findConversation(_state, conversationId);
    if (conversation) {
      Vue.set(conversation, 'last_activity_at', lastActivityAt);
    }
  },

  [types.SET_INTERNAL_CHAT_CONVERSATION_UNREAD_COUNT](
    _state,
    { conversationId, unreadCount }
  ) {
    const conversation = findConversation(_state, conversationId);
    if (conversation) {
      Vue.set(conversation, 'unread_count', unreadCount);
    }
  },

  [types.INCREMENT_INTERNAL_CHAT_UNREAD_COUNT](_state, conversationId) {
    const conversation = findConversation(_state, conversationId);
    if (conversation) {
      Vue.set(
        conversation,
        'unread_count',
        (conversation.unread_count || 0) + 1
      );
    }
  },

  [types.SET_INTERNAL_CHAT_MESSAGES](_state, { conversationId, messages }) {
    Vue.set(_state.messagesByConversationId, conversationId, messages);
  },

  [types.PREPEND_INTERNAL_CHAT_MESSAGES](_state, { conversationId, messages }) {
    const existing = _state.messagesByConversationId[conversationId] || [];
    Vue.set(_state.messagesByConversationId, conversationId, [
      ...messages,
      ...existing,
    ]);
  },

  [types.ADD_INTERNAL_CHAT_MESSAGE](_state, { conversationId, message }) {
    const existing = _state.messagesByConversationId[conversationId] || [];
    const alreadyExists = existing.some(item => item.id === message.id);

    if (alreadyExists) {
      return;
    }

    Vue.set(_state.messagesByConversationId, conversationId, [
      ...existing,
      message,
    ]);
  },

  [types.MARK_INTERNAL_CHAT_MESSAGES_READ](
    _state,
    { conversationId, readerId, readAt }
  ) {
    const messages = _state.messagesByConversationId[conversationId];
    if (!messages) {
      return;
    }

    const updatedMessages = messages.map(message => {
      if (message.sender_id === readerId || message.status === 'read') {
        return message;
      }

      return { ...message, status: 'read', read_at: readAt };
    });

    Vue.set(_state.messagesByConversationId, conversationId, updatedMessages);
  },

  [types.SET_INTERNAL_CHAT_ACTIVE_CONVERSATION_ID](_state, conversationId) {
    _state.activeConversationId = conversationId;
  },

  [types.CLEAR_INTERNAL_CHAT_ACTIVE_CONVERSATION_ID](_state) {
    _state.activeConversationId = null;
  },

  [types.SET_INTERNAL_CHAT_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
